import React, { useEffect, useMemo, useState } from 'react';
import {
  Bot, KeyRound, Palette, ShieldCheck, Gauge, Sparkles, Save, Plug, Server, Cloud,
} from 'lucide-react';
import { toast } from 'sonner';
import { aiAssistantAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { StatCard } from './common/StatCard';
import { CardSkeleton } from './common/States';
import { getErrorMessage } from '../lib/utils';

const ALL_ROLES = ['workspace_admin', 'hr_admin', 'hr_manager', 'manager', 'employee'];
const ALL_MODULES = ['leave', 'attendance', 'policies', 'holidays', 'helpdesk', 'documents', 'payroll', 'performance'];

const Section = ({ icon: Icon, title, desc, children }) => (
  <Card>
    <CardHeader className="border-b border-border/60 pb-3">
      <CardTitle className="flex items-center gap-2 text-sm font-semibold">
        <span className="grid h-7 w-7 place-items-center rounded-lg bg-primary/10 text-primary"><Icon className="h-4 w-4" /></span>
        {title}
      </CardTitle>
      {desc && <p className="pl-9 text-xs text-muted-foreground">{desc}</p>}
    </CardHeader>
    <CardContent className="space-y-4 pt-4">{children}</CardContent>
  </Card>
);

const Chip = ({ active, onClick, children }) => (
  <button type="button" onClick={onClick}
    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${active ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-muted-foreground hover:border-primary/40'}`}>
    {children}
  </button>
);

export default function AIAssistantSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [providers, setProviders] = useState([]);
  const [usage, setUsage] = useState(null);
  const [form, setForm] = useState(null);
  const [apiKey, setApiKey] = useState('');

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    (async () => {
      try {
        const [s, p, u] = await Promise.all([
          aiAssistantAPI.getSettings(),
          aiAssistantAPI.getProviders(),
          aiAssistantAPI.getUsage().catch(() => ({ data: null })),
        ]);
        setForm(s.data);
        setProviders(p.data?.providers || []);
        setUsage(u.data);
      } catch (e) {
        toast.error(getErrorMessage(e, 'Failed to load AI settings'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const activeProvider = useMemo(() => providers.find((p) => p.id === form?.provider), [providers, form]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.api_key_set;
      delete payload.tenant_id;
      if (apiKey) payload.api_key = apiKey;   // only send when changed
      const res = await aiAssistantAPI.updateSettings(payload);
      setForm(res.data); setApiKey('');
      toast.success('AI Assistant settings saved');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to save settings'));
    } finally { setSaving(false); }
  };

  const testProvider = async () => {
    setTesting(true);
    try {
      if (apiKey || JSON.stringify(form)) await save();
      const res = await aiAssistantAPI.testProvider();
      if (res.data?.ok) toast.success(`Connected to ${res.data.provider} (${res.data.latency_ms}ms)`);
      else toast.error(`Provider test failed: ${res.data?.error || 'unknown error'}`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Provider test failed'));
    } finally { setTesting(false); }
  };

  if (loading || !form) {
    return <div className="space-y-4"><CardSkeleton rows={4} /><CardSkeleton rows={5} /></div>;
  }

  const toggleIn = (key, val) => {
    const list = form[key] || [];
    set({ [key]: list.includes(val) ? list.filter((x) => x !== val) : [...list, val] });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-foreground">
            <Bot className="h-6 w-6 text-primary" /> AI Assistant
          </h2>
          <p className="text-sm text-muted-foreground">Configure your multi-provider, tenant-branded AI assistant.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2">
            <Label className="text-xs">Enabled</Label>
            <Switch checked={!!form.enabled} onCheckedChange={(v) => set({ enabled: v })} />
          </div>
          <Button variant="outline" onClick={testProvider} disabled={testing}><Plug className="mr-2 h-4 w-4" />{testing ? 'Testing…' : 'Test'}</Button>
          <Button onClick={save} disabled={saving}><Save className="mr-2 h-4 w-4" />{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </div>

      {usage && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Requests (mo)" value={usage.requests} icon={Gauge} tone="primary" hint={usage.request_limit ? `Limit ${usage.request_limit}` : 'Unlimited'} />
          <StatCard label="Tokens (mo)" value={usage.tokens} icon={Sparkles} tone="info" hint={usage.token_limit ? `Limit ${usage.token_limit}` : 'Unlimited'} />
          <StatCard label="Est. Cost (mo)" value={`$${(usage.cost_usd || 0).toFixed(2)}`} icon={Cloud} tone="success" animate={false} />
          <StatCard label="Active Users" value={usage.active_users} icon={ShieldCheck} tone="purple" hint={`${usage.errors} errors`} />
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Section icon={Server} title="Provider & Model" desc="Choose any hosted or self-hosted LLM. Adding a provider needs no code changes.">
          <div>
            <Label>Provider</Label>
            <Select value={form.provider} onValueChange={(v) => { const p = providers.find((x) => x.id === v); set({ provider: v, model: p?.default_model || '', base_url: p?.default_base_url || '' }); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {providers.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    <span className="flex items-center gap-2">{p.edition === 'community' ? <Server className="h-3.5 w-3.5" /> : <Cloud className="h-3.5 w-3.5" />}{p.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {activeProvider && (
              <p className="mt-1 text-xs text-muted-foreground">
                {activeProvider.builtin
                  ? 'Free & built-in — no API key or setup. Answers are grounded in your live HR data.'
                  : activeProvider.edition === 'community' ? 'Self-hosted / free — no vendor lock-in.' : 'Hosted provider — API key encrypted at rest.'}
              </p>
            )}
          </div>
          {activeProvider?.builtin ? (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs text-muted-foreground">
              <p className="font-medium text-foreground">No configuration required.</p>
              <p className="mt-1">This free assistant answers from your workspace's persisted HR analytics
                (attrition, hiring, payroll, engagement) — no API key, model, or external calls. Upgrade to a
                hosted provider anytime for open-ended, generative conversations.</p>
            </div>
          ) : (
            <div>
              <Label>Model</Label>
              <Input value={form.model || ''} onChange={(e) => set({ model: e.target.value })} placeholder={activeProvider?.default_model || 'model name'} />
              {activeProvider?.popular_models?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {activeProvider.popular_models.map((m) => <Chip key={m} active={form.model === m} onClick={() => set({ model: m })}>{m}</Chip>)}
                </div>
              )}
            </div>
          )}
          {activeProvider?.uses_base_url && (
            <div>
              <Label>Base URL</Label>
              <Input value={form.base_url || ''} onChange={(e) => set({ base_url: e.target.value })} placeholder={activeProvider?.default_base_url} />
            </div>
          )}
          {activeProvider?.requires_api_key && (
            <div>
              <Label className="flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" /> API Key</Label>
              <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                placeholder={form.api_key_set ? '•••••••• (saved — leave blank to keep)' : 'Enter API key'} />
              <p className="mt-1 text-xs text-muted-foreground">Encrypted at rest. Never returned to the browser.</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Temperature</Label><Input type="number" step="0.1" min="0" max="2" value={form.temperature} onChange={(e) => set({ temperature: parseFloat(e.target.value) })} /></div>
            <div><Label>Max Tokens</Label><Input type="number" value={form.max_tokens} onChange={(e) => set({ max_tokens: parseInt(e.target.value, 10) })} /></div>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border bg-muted/40 px-3 py-2">
            <Label className="text-xs">Streaming responses</Label>
            <Switch checked={!!form.streaming} onCheckedChange={(v) => set({ streaming: v })} />
          </div>
        </Section>

        <Section icon={Palette} title="Branding" desc="Give your assistant a name, look and voice.">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Assistant Name</Label><Input value={form.assistant_name || ''} onChange={(e) => set({ assistant_name: e.target.value })} placeholder="e.g. HR Genie, Ava" /></div>
            <div><Label>Theme Color</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.theme_color || '#3b4fb8'} onChange={(e) => set({ theme_color: e.target.value })} className="h-10 w-12 cursor-pointer rounded border border-border bg-card" />
                <Input value={form.theme_color || ''} onChange={(e) => set({ theme_color: e.target.value })} />
              </div>
            </div>
          </div>
          <div><Label>Avatar URL (optional)</Label><Input value={form.avatar_url || ''} onChange={(e) => set({ avatar_url: e.target.value })} placeholder="https://…" /></div>
          <div><Label>Welcome Message</Label><Textarea value={form.welcome_message || ''} onChange={(e) => set({ welcome_message: e.target.value })} rows={2} /></div>
          <div><Label>Suggested Prompts (one per line)</Label>
            <Textarea rows={4} value={(form.suggested_prompts || []).join('\n')} onChange={(e) => set({ suggested_prompts: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })} />
          </div>
          <div><Label>System Prompt</Label><Textarea rows={4} value={form.system_prompt || ''} onChange={(e) => set({ system_prompt: e.target.value })} /></div>
        </Section>

        <Section icon={ShieldCheck} title="Permissions & Knowledge" desc="Who can use it and which HRMS knowledge it may draw on.">
          <div>
            <Label>Allowed Roles</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {ALL_ROLES.map((r) => <Chip key={r} active={(form.allowed_roles || []).includes(r)} onClick={() => toggleIn('allowed_roles', r)}>{r.replace('_', ' ')}</Chip>)}
            </div>
          </div>
          <div>
            <Label>Enabled Knowledge Modules</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {ALL_MODULES.map((m) => <Chip key={m} active={(form.enabled_modules || []).includes(m)} onClick={() => toggleIn('enabled_modules', m)}>{m}</Chip>)}
            </div>
          </div>
        </Section>

        <Section icon={Gauge} title="Usage Limits & Retention" desc="Guardrails for cost and data retention (0 = unlimited).">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Monthly Request Limit</Label><Input type="number" value={form.monthly_request_limit || 0} onChange={(e) => set({ monthly_request_limit: parseInt(e.target.value, 10) || 0 })} /></div>
            <div><Label>Monthly Token Limit</Label><Input type="number" value={form.monthly_token_limit || 0} onChange={(e) => set({ monthly_token_limit: parseInt(e.target.value, 10) || 0 })} /></div>
          </div>
          <div><Label>Conversation Retention (days)</Label><Input type="number" value={form.retention_days || 90} onChange={(e) => set({ retention_days: parseInt(e.target.value, 10) || 90 })} /></div>
          {usage?.by_model?.length > 0 && (
            <div className="rounded-lg border border-border">
              <div className="border-b border-border bg-muted/40 px-3 py-2 text-xs font-semibold text-muted-foreground">Usage by model (this month)</div>
              <div className="divide-y divide-border">
                {usage.by_model.map((r, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="truncate text-foreground">{r.model || r.provider}</span>
                    <span className="text-muted-foreground">{r.requests} req · {r.tokens} tok · {r.avg_latency_ms}ms</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
